import { getCachedViewSnapshot } from '@databases/database-state/state/view';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import cellSelectionsSlice from '../cellSelections/slice';
import { type AppRootState } from '../types';

type ListenerApi = {
  getState: () => AppRootState;
  dispatch: (action: unknown) => unknown;
};

function publishSelectionToRovo(selection: string[]) {
  waitForContentAPI().then((contentAPI) => {
    contentAPI?.publishDatabaseReferencingContent?.(selection);
  });
}

function computeResolvedSelection(
  state: AppRootState,
  contentId: string,
  layoutType: DatabaseLayoutType
): string[] {
  const { selectedEntireFields, selectedEntireEntries, selectedValues } =
    state.cellSelections;
  const database = selectDatabaseByContentIdOptional(state, { contentId });

  if (!database || isBrokenDatabase(database)) {
    return [];
  }

  const hasEntireFieldsSelected = selectedEntireFields.length > 0;
  const hasEntireEntriesSelected = selectedEntireEntries.length > 0;
  const hasValuesSelected = selectedValues.length > 0;

  const hasSelection =
    hasEntireFieldsSelected || hasEntireEntriesSelected || hasValuesSelected;

  if (!hasSelection) {
    return [];
  }

  if (hasEntireFieldsSelected) {
    const selectedFieldIdsInView = database.fields.filter((id) =>
      selectedEntireFields.includes(id)
    );
    return selectedFieldIdsInView.map((fieldId) => `field:${fieldId}`);
  }

  if (hasEntireEntriesSelected) {
    const selectedEntryIdsInView = database.entries.filter((id) =>
      selectedEntireEntries.includes(id)
    );
    const entryType = layoutType === DatabaseLayoutType.TABLE ? 'entry' : 'card';
    return selectedEntryIdsInView.map((entryId) => `${entryType}:${entryId}`);
  }

  const selectedValuesInView = selectedValues.filter(
    (v) => database.entries.includes(v.entryId) && database.fields.includes(v.fieldId)
  );
  return selectedValuesInView.map((value) => `cell:${value.entryId}:${value.fieldId}`);
}

function publishResolvedSelection(listenerApi: ListenerApi, contentId: string) {
  const state = listenerApi.getState();
  const layoutType = getCachedViewSnapshot().settings.layout.type;
  const selection = computeResolvedSelection(state, contentId, layoutType);
  publishSelectionToRovo(selection);
}

export const resolvedSelectionMiddleware = createListenerMiddleware();

resolvedSelectionMiddleware.startListening({
  predicate: (action) =>
    isFeatureFlagEnabled('databases-rovo-referencing-content') &&
    (cellSelectionsSlice.actions.selectValues.match(action) ||
      cellSelectionsSlice.actions.selectEntries.match(action) ||
      cellSelectionsSlice.actions.selectFields.match(action)),
  effect: (action, listenerApi) => {
    const contentId = action.payload.contentId;
    publishResolvedSelection(listenerApi as ListenerApi, contentId);
  },
});

resolvedSelectionMiddleware.startListening({
  predicate: (action) =>
    isFeatureFlagEnabled('databases-rovo-referencing-content') &&
    cellSelectionsSlice.actions.deselectAll.match(action),
  effect: () => {
    publishSelectionToRovo([]);
  },
});

resolvedSelectionMiddleware.startListening({
  predicate: (action) =>
    isFeatureFlagEnabled('databases-rovo-referencing-content') &&
    action.type === 'databases/applyDatabaseDiff',
  effect: (action, listenerApi) => {
    const { contentId, diff } = action.payload;
    const hasStructuralChanges =
      diff.changedFieldIds.length > 0 || diff.changedEntryIds.length > 0;
    if (hasStructuralChanges) {
      publishResolvedSelection(listenerApi as ListenerApi, contentId);
    }
  },
});
